<script setup>
import { i18n } from 'boot/i18n';
import { computed } from 'vue';

defineOptions({ name: 'ReviewSuggestedAssignmentsTable' });

const props = defineProps({
  rows: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits([
  'toggle-all',
  'toggle-row',
  'update-cohort',
]);

const pagination = {
  rowsPerPage: 10,
  sortBy: 'currentCohortName',
  descending: false,
};

function getMatchedCriteriaLabels(labels) {
  if (!Array.isArray(labels)) {
    return '';
  }

  return labels
    .map((label) => String(label).split(/\s*[:=]\s*/, 1)[0].trim())
    .filter(Boolean)
    .join(', ');
}

const selectedRows = computed(() => (
  props.rows.filter((row) => row.selected === true)
));

const columns = [
  {
    name: 'clientName',
    label: i18n.t('settings.careModel.cohorts.review.client'),
    align: 'left',
    field: (row) => row.client?.clientName || '',
    sortable: true,
  },
  {
    name: 'currentCohortName',
    label: i18n.t('settings.careModel.cohorts.suggestedUpdates.currentCohort'),
    align: 'left',
    field: (row) => row.currentCohortName || '—',
    sortable: true,
  },
  {
    name: 'suggestedCohort',
    label: i18n.t('settings.careModel.cohorts.review.suggestedCohort'),
    align: 'left',
    field: (row) => row.suggestedCohort?.cohortName || '',
  },
  {
    name: 'matches',
    label: i18n.t('settings.careModel.cohorts.review.parameterMatches'),
    align: 'left',
    field: (row) => row.matchedLabels || [],
    format: (value) => getMatchedCriteriaLabels(value),
  },
];

function onSelectionUpdate(rows) {
  const selectedIds = new Set(
    rows.map((row) => row.client?.id),
  );

  props.rows.forEach((row) => {
    emit(
      'toggle-row',
      row.client?.id,
      selectedIds.has(row.client?.id),
    );
  });
}
</script>

<template>
  <q-table
    :rows="rows"
    :columns="columns"
    :selected="selectedRows"
    selection="multiple"
    :row-key="(row) => row.client.id"
    color="primary"
    flat
    :pagination="pagination"
    class="col-12"
    @update:selected="onSelectionUpdate"
  >
    <template #body-cell-clientName="cellProps">
      <q-td :props="cellProps">
        {{ cellProps.row.client?.clientName || '—' }}
      </q-td>
    </template>

    <template #body-cell-currentCohortName="cellProps">
      <q-td :props="cellProps">
        {{ cellProps.row.currentCohortName || '—' }}
      </q-td>
    </template>

    <template #body-cell-suggestedCohort="cellProps">
      <q-td :props="cellProps">
        <q-select
          v-if="cellProps.row.matchedCohorts?.length"
          :model-value="cellProps.row.selectedCohortId"
          :options="cellProps.row.matchedCohorts"
          option-label="cohortName"
          option-value="id"
          emit-value
          map-options
          dense
          outlined
          @update:model-value="emit(
            'update-cohort',
            cellProps.row.client.id,
            $event,
          )"
        />

        <span v-else>
          {{ cellProps.row.suggestedCohort?.cohortName || '—' }}
        </span>
      </q-td>
    </template>

    <template #body-cell-matches="cellProps">
      <q-td :props="cellProps">
        {{ getMatchedCriteriaLabels(cellProps.row.matchedLabels) || '—' }}
      </q-td>
    </template>
  </q-table>
</template>
